import { useState, useCallback } from 'react'

import { useGetApplicantQuery } from '@/lib/apollo'
import type { Applicant } from '@/lib/apollo/query/get-applicant'
import QUERIES from '@/lib/graphql'
import type { Application } from '@/types/Activity'

import { graphQLQuery } from './hooks'

export type FetchSingleApplication = (
  customerId: string,
  subjectId: string,
) => Promise<void>

const useApplicantSubject = () => {
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const [applicant, setApplicant] = useState<Applicant | undefined>(undefined)
  const [application, setApplication] = useState<Application | undefined>(undefined)

  const [fetchApplicant] = useGetApplicantQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
  })

  const fetchSingleApplication = useCallback<FetchSingleApplication>(
    async (customerId: string, subjectId: string) => {
      setLoading(true)
      setApplication(undefined)
      setApplicant(undefined)
      setError(undefined)

      try {
        const { data: applicantData } = await fetchApplicant({
          variables: {
            customerId,
            subjectId,
          },
        })

        if (!applicantData) {
          throw Error('No subject was retrieved')
        }

        setApplicant(applicantData?.getApplicant)

        const getSingleApplicationResponse = await graphQLQuery({
          query: QUERIES.getSingleApplication,
          variables: {
            customerId,
            applicationId: applicantData?.getApplicant.applicationId,
            subjectId,
          },
          endpoint: `${import.meta.env.VITE_REPORTS_GRAPHQL}`,
        })

        const data = await getSingleApplicationResponse.json()

        if (!data || !data.data) {
          throw Error('Get single application returned undefined')
        }

        setApplication(data.data.getDashboardApplication)
      } catch (e) {
        console.error(e)
        setError('Failed to retrieve applicant')
      } finally {
        setLoading(false)
      }
    },
    [fetchApplicant, setLoading, setError, setApplication, setApplicant],
  )

  return {
    loading,
    error,
    applicant,
    application,
    fetchSingleApplication,
  }
}

export default useApplicantSubject
